/**
 * The capability's lifetime, as fixed by the accepted product contract.
 *
 * Used as the fallback when the terminal's own clock cannot be trusted (see
 * {@link initialRemainingMs}).
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { REVIEW_TTL_MS } from "./expiry";
 *
 * REVIEW_TTL_MS / 60_000; // => 10
 * ```
 */
export const REVIEW_TTL_MS = 10 * 60_000;

/**
 * How long before the deadline the quiet notice appears.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { NEAR_EXPIRY_MS } from "./expiry";
 *
 * NEAR_EXPIRY_MS / 60_000; // => 2
 * ```
 */
export const NEAR_EXPIRY_MS = 2 * 60_000;

/**
 * How much of the review's lifetime is left at the moment its data arrived.
 *
 * The DTO carries an absolute `expiresAt` but no server clock, so the only available reading is
 * `expiresAt - localNow` — which is wrong by exactly the terminal's clock skew. A kiosk whose clock
 * runs fast would compute a negative remainder and blank the review the instant it loaded; one
 * running slow would never warn at all. Both are worse than ignoring the reading, so a result
 * outside the contractual lifetime is treated as unusable and the known TTL is used instead. The
 * server remains the only authority on the real deadline; this figure only drives presentation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * import { initialRemainingMs } from "./expiry";
 *
 * initialRemainingMs(Date.parse("2026-07-22T10:05:00Z"), Date.parse("2026-07-22T10:00:00Z"));
 * // => 300000
 * ```
 */
export function initialRemainingMs(expiresAtMs: number, localNowMs: number): number {
  const raw = expiresAtMs - localNowMs;
  if (!Number.isFinite(raw) || raw <= 0 || raw > REVIEW_TTL_MS) return REVIEW_TTL_MS;
  return raw;
}
